<script type="text/jsx" lang="jsx">
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { BILLING_CYCLE, planTypes, BASE_PLAN_TYPES, DEFAULT_FREE_TRIAL_DAYS, BillingCycleEnum } from '@/common/constants';
import { routePathMap } from '@/router/constant';
import { get, toUpper, omit } from 'lodash';
import { dateTimeFormatWithUtcOffset } from '@/utils/dateformat';
import { getDefaultPayApmInfo } from "@/store/modules/payment";
import { plansof } from '@/utils/chain';
import { pageConfig } from '@/common/service/page-config';
import { PLAN_TYPES_WITH_MULTI_BASE_PLANS } from '@/plans/ROOM_CONNECTOR/common/utils';
import { billingCycleToValueMap } from '@/utils';
import { getMultipleRatePlanSumPrice } from '@/store/modules/shopping-cart';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { isFromShoppingCart, isProductCanRemove } from '@/views/config/shopping-cart/persist/plan-remove';
import { persistCartConstant } from '@/views/config/shopping-cart/persist/persist-cart';
import ZmButton from '@/components/zoom-ui-plus/button/index.vue';
import NextChargeDate from '@/views/config/shopping-cart/next-charge-date.vue';
import PlanDiscountList from '@/views/config/shopping-cart/discount/plan-discount-list.vue';
import { getEffectiveAmount } from '@/utils/downsell-price';
import NextRenewalDate from '@/views/config/shopping-cart/next-renewal-date.vue';
import { getSubscriptionCycleDesc, getNewPlanUnitWithQuantity } from '@/common/product-unit';
import { isFromCampaignLink } from '@/views/config/coupon/helper/utils';
import flow from '@/flows';
import { VIEWS } from '@/common/constants';

// render priority list
export const renderKeys = ['renderPrice', 'renderTip', 'renderAgreement', 'renderSuccessTitle'];

// render controls
export const props = renderKeys.reduce((acc, x) => {
  acc[x] = {
    type: Boolean,
    default: false,
  };
  return acc;
}, {});

export const CART_DISPLAY_MODE = {
  BOUGHT: 'bought', // only bought
  DELTA: 'delta', // only delta added
  SUM: 'sum', // both bought and added
};

export default {
  components: {
    PlanDiscountList,
    NextRenewalDate,
  },
  props: {
    ...props,
    __pkg: Object, // plan config package of @/plans
    filterKeys: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    const $context = this.$context;
    const isModelBuyNow = flow.view() === VIEWS.CONFIG && this.$context.is.modal;
    if (!$context.is.checkoutPage && !isModelBuyNow) return;
    const eventType = 'plan-edit-action';
    $context.$off(eventType).$on(eventType, () => this.ma_edit_action());
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'discountWithAmountMap', 'boughtPlans', 'apmInfo', 'partnerRefer', 'eligiblePartnerReferOffer', 'migratePlanState', 'couponConfiguration']),
    mode() {
      return CART_DISPLAY_MODE.BOUGHT;
    },
    mx_plans() {
      const key = this.$options.key;
      const type = planTypes[key];
      return this.getSelectedPlans.filter((x) => toUpper(x.type) === toUpper(type));
    },
    mx_startDate() {
      // TODO consider paid2paid date
      return new Date();
    },
    mx_isLastBasePlan() {
      // is this the last base plan that should not be removed
      const key = this.$options.key;
      const type = planTypes[key];
      if (!BASE_PLAN_TYPES.includes(type)) return false; // is not base plan
      let baseTypes = this.mx_plans.map((x) => x.type).filter((x) => BASE_PLAN_TYPES.includes(x)); // base plans among these
      if (baseTypes.length === 1 && baseTypes[0] === type) return true; // only when

      return false;
    },
    // For CRC special case that need to check the last same billing cycle base plan since it could be more than 1 base plan
    mx_isLastBasePlanForTheSameBillingCycle() {
      // Some plan may has more than 1 base plan dependencies like CRC
      const hasThePlanNeedsMultiBasePlan = plansof(this.getSelectedPlans).filter((plan) => PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(plan.type)).has();
      const currentPrimaryPlan = plansof(this.getSelectedPlans).filter((plan) => PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(plan.type)).get(0);
      // Get current mixin base plan type like zoom_meeting zoomroom
      const key = this.$options.key;
      const type = planTypes[key];
      // e.g CRC as the primary plan
      if(hasThePlanNeedsMultiBasePlan) {
        // CRC billing cycle
        const currentPrimaryPlanBillingCycle = currentPrimaryPlan.billing_cycle;
        // Filter base plans which are the same billing cycle or annual > monthly with CRC
        const SameBillingCycleBasePlans = plansof(this.getSelectedPlans).type(...BASE_PLAN_TYPES).filter(basePlan => BillingCycleEnum[basePlan.billing_cycle] >= BillingCycleEnum[currentPrimaryPlanBillingCycle]);
        // Find the last billing cycle
        const theLastSameBillingCycleBasePlanType = SameBillingCycleBasePlans.get(-1).type;
        // is this plan now
        return theLastSameBillingCycleBasePlanType === type
      }

      return false;
    },
  },
  methods: {
    ...mapMutations(['setApmInfo']),
    ...mapActions(['checkProMonthlyAndYearlyPriceChange']),
    /**
     * Get query parameters for edit action
     * For persist shopping bag v2, region is handled by config-GS.vue/mx-edit.js
     * so we don't pass it in URL to avoid conflicts
     *
     * @param {Object} plan - Plan object
     * @param {boolean} includeCurrentStep - Whether to include currentStep parameter (for modal mode)
     * @returns {Object} Query parameters
     */
    getEditQueryParams(plan, includeCurrentStep = false) {
      const baseQuery = {
        region: plan?.region,
        ...(includeCurrentStep && { currentStep: 'config' })
      };

      // For persist shopping bag v2, omit region to avoid conflicts
      if (persistCartConstant.enablePersistShoppingBagForV2) {
        return omit(baseQuery, 'region');
      }

      return baseQuery;
    },
    _dispatch(type, ...args) {
      const planType = get(args, '0.type', '');
      let key = this.$options.key;
      if(planType && planTypes[planType]) {
        key = planTypes[planType];
      }
      this.$emit(type, key, ...args);
    },
    mx_main_title(title, value, parentCategory) {
      return <h3 class="opc-cart-item__title __PLACEHOLDER_first_title__">
        <span data-parent-category-title={parentCategory}>{ title }</span>
        {value !== undefined && <span class="opc-cart-item__value text-no-capitalize">{value}</span>}
      </h3>
    },
    mx_title(title, price, planCode, parentCategory, classList) {
      let _price = typeof price === 'number' ? this.$money(price) : price;
      return (
        <h3 class={'opc-cart-item__title __PLACEHOLDER_first_title__' + (classList ? ' ' + classList.join(' ') : '')}>
          <span className="opc-cart-item__subtitle"
            data-parent-category-title={parentCategory}
            data-plan-code-title={planCode}>{title}</span>
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{_price}</span>}
        </h3>
      );
    },
    mx_per_license_price(plan) {
      // ! note not support multiple rate plans
      // todo CART_DISPLAY_MODE.DELTA & CART_DISPLAY_MODE.BOUGHT
      return this.mx_price(plan) / (plan?.cartInfo?.selected_number || 1);
    },
    mx_price(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      if (boughtPlan?.ratePlans?.length > 1) {
        return getMultipleRatePlanSumPrice(boughtPlan, plan.cartInfo.selected_number);
      }

      // Create context for downsell logic - try to get current license from bought plan
      const context = {
        currentLicense: boughtPlan?.cartInfo?.selected_number ||
                       this.$context?.currentEditPlan?.cartInfo?.selected_number ||
                       0
      };

      // Get effective amount considering downsell logic
      const effectiveAmount = getEffectiveAmount(plan, context);

      if (plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
        return effectiveAmount / billingCycleToValueMap[plan.billing_cycle] * plan.cartInfo.selected_number;
      }

      return plan.cartInfo.selected_number * effectiveAmount;
    },
    mx_price_bought(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      return this.mx_price(boughtPlan);
    },
    mx_price_delta(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return this.mx_price(plan) - this.mx_price(boughtPlan);
    },
    mx_price$(plan){
      if(this.mode === CART_DISPLAY_MODE.DELTA) return this.mx_price_delta(plan);
      if(this.mode === CART_DISPLAY_MODE.BOUGHT) return this.mx_price_bought(plan);
      return this.mx_price(plan);
    },
    mx_subTitle(title, price, planCode) {
      let _price = typeof price === 'number' ? this.$money(price) : price;
      return (
        <h3 class="opc-cart-item__sub-title __PLACEHOLDER_sub_title__">
          <span data-plan-code-title={planCode}>{title}</span>
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{_price}</span>}
        </h3>
      );
    },
    // FREE TRIAL - shopping cart title - show Title + 'Free Trial' as free trial plan = paid plan + 0 price
    mx_freeTrialTitle(title, planCode, parentCategory) {
      return (
        <h3 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">
          <span data-plan-code-title={planCode} data-parent-category-title={parentCategory}>{this.$t('billing.trial.title', [title])}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(0)}</span>
        </h3>
      );
    },
    mx_freeTrialStartDate(startTime = new Date()) {
      // show pst data time
      return (
        <div class="opc-cart-item__text">
          {this.$t('billing.trial.start_date', [dateTimeFormatWithUtcOffset(startTime, {offset: -8})])}
        </div>
      );
    },
    mx_freeTrialDesc(trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      return (
        <div class="opc-cart-item__text opc-cart-item__duration-text text-lowercase">
          {this.$t('billing.trial.duration_desc_cart', [trialDays])}
        </div>
      );
    },
    mx_new_license_with_amount(unitWithQuantity, amount) {
      if (!amount) return (
        <div class="opc-cart-item__text" aria-label={`${unitWithQuantity}`}>
          { unitWithQuantity }
        </div>
      );;
      let _amount = typeof amount === 'number' ? this.$money(amount) : amount;
      return (
        <div class="opc-cart-item__text" aria-label={`${unitWithQuantity} times ${_amount}`}>
          { unitWithQuantity } x { _amount }
        </div>
      );
    },
    mx_new_license_without_amount(plan, num) {
      const unitWithQuantity = getNewPlanUnitWithQuantity(plan.unit, num);
      return (
        <div class="opc-cart-item__text" aria-label={`${unitWithQuantity}`}>
          { unitWithQuantity }
        </div>
      );
    },
    mx_new_license(plan, num) {
      if (
        plan.isFreeTrial || plan.isOnlineFreeTrial || // free trail case
        (Array.isArray(plan.ratePlans) && plan.ratePlans.length > 1) // multiple rate plan case
        || this.$context.is.align // align with existing custom price or increased price etc.
      ) {
        return this.mx_new_license_with_amount(getNewPlanUnitWithQuantity(plan.unit, num));
      }
      return this.mx_new_license_with_amount(getNewPlanUnitWithQuantity(plan.unit, num), this.mx_per_license_price(plan));
    },
    mx_license(num, unit = this.$t('billing.planform.unit.user')) {
      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    mx_license_bought(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return boughtPlan?.cartInfo?.selected_number;
    },
    mx_license_delta(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return plan.cartInfo.selected_number - boughtPlan?.cartInfo?.selected_number;
    },
    mx_license$(plan){
      let num;
      if(this.mode === CART_DISPLAY_MODE.DELTA) {
        num = this.mx_license_delta(plan);
      }else if(this.mode === CART_DISPLAY_MODE.BOUGHT){
        num = this.mx_license_bought(plan);
      }else{
        num = plan?.cartInfo?.selected_number;
      };
      // return this.mx_license(num);
      return this.mx_new_license(plan, num);
    },
    mx_renewal_date() {
      return [
        <next-renewal-date />,
      ];
    },
    mx_cycle(cycle, remark = '') {
      const label = getSubscriptionCycleDesc(cycle);
      if (!label) return '';
      return [
        <div class="opc-cart-item__text first-capitalize opc-cart-item__cycle"> { label }
          {remark ? <span class='text-no-capitalize'>&nbsp;({remark})</span> : ''}
        </div>,
        cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY &&
        (this.$context.is.buyNow || this.$context.is.basic2pro) &&
        <NextChargeDate billing-cycle={cycle} />,
      ];
    },
    // Render discount items using DiscountList component
    mx_discount(planCode) {
      return <PlanDiscountList planCode={planCode} />;
    },
    ma_edit_action(plan) {
      // PRO INCREASE - Check Pro Monthly & Yearly Price change before redirect to config plan page
      // Fix the special case: Current sold to is US, then edit to Vietnam without saving, then click edit button in shopping-cart
      this.checkProMonthlyAndYearlyPriceChange();

      const isModalMode = this.$context.is.modal;
      const query = this.getEditQueryParams(plan, isModalMode);

      if (isModalMode) {
        this.$router.replace({
          path: routePathMap.config,
          query,
        });
      } else {
        this.$router.push({
          path: routePathMap.config,
          query,
        });
      }
      this.$store.commit('setRouterTempData', {
        targetPlan: plan,
      });
      if (this.apmInfo.isApm) {
        this.$nextTick(() => {
          this.setApmInfo(getDefaultPayApmInfo())
        });

      }
      this.$tracking.orderSummary(
        `edit ${this.__pkg.components?.addon ? 'add-on product' : 'product'} - ${this.$t(plan?.planNameKey)}`,
      );
    },
    mx_edit(plan, cycle) {
      if (!this.$context.is.checkoutPage || this.$context.is.switchCurrency) return;
      let planTitle = `${this.$t(plan?.planNameKey)} ${cycle ? getSubscriptionCycleDesc(cycle) : ''}`;
      if (plan.isOnlineFreeTrial && plan.trialDays) {
        planTitle = `${this.$t('billing.trial.title', [planTitle])}`;
      }
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.edit')} ${planTitle}`}
          onClick={() => this.ma_edit_action(plan)}
        >
          {this.$t('billing.common.edit')}
        </ZmButton>
      );
    },
    mx_remove_persist_cart(plan) {
      if(!isProductCanRemove(plan) || !this.$context.is.checkoutPage || this.$context.is.switchCurrency) {
        return;
      }
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.__pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    mx_remove(plan, cycle) {
      if(isFromShoppingCart()) {
        return this.mx_remove_persist_cart(plan);
      }
      if (!this.$context.is.checkoutPage || this.$context.is.switchCurrency) return;
      const configType = pageConfig?.editConfig?.planType;
      if(configType === plan.type && plansof(this.getSelectedPlans).type(plan.type).has(1)) return; // base plan can't remove if query contains
      // for campaign link, if only one plan in shopping cart, don't show remove button
      if(
        isFromCampaignLink() &&
        (this.$context?.is?.buyNow || this.$context?.is?.basic2pro) &&
        this.getSelectedPlans?.length <= 1
      ) {
        return null;
      }
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)} ${cycle ? getSubscriptionCycleDesc(cycle) : ''}`}
          onClick={() => {
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.__pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    mx_expire_date(date) {
      if (!date) return '';
      return <div class="opc-cart-item__text first-capitalize"> {date}</div>;
    },
  },
  render() {
    const fn = renderKeys.find((x) => this[x]);
    if (!fn) return null;

    // Use 'in' operator to check method existence without triggering Vue warnings
    const renderMethod = `$${fn}`;
    if (!(renderMethod in this)) return null;

    return <div>{this[renderMethod]()}</div>;
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-cart-item {
  margin-bottom: 16px;
  @include media(mobile, tablet) {
    margin-bottom: 16px;
  }
  &__value {
    margin-left: auto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    color: var(--zoom-color-text-stronger-neutral);
    @include media(mobile, tablet) {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__title {
    text-align: left;
    display: flex;
    margin-bottom: 4px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 16px;
    font-style: normal;
    font-weight: 590;
    line-height: 20px;

    @include media(mobile, tablet) {
      font-size: 14px;
      line-height: 1.5;
    }
  }
  &__sub-title {
    text-align: left;
    font-weight: 700;
    font-size: 14px;
    line-height: 22px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    display: flex;
    @include media(mobile, tablet) {
      font-weight: 500;
      line-height: 1.5;
    }
  }
  &__text {
    font-style: normal;
    color: var(--zoom-color-text-neutral, #686F79);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }
  &__duration-text {
    color: var(--zoom-color-text-success, #247F40);
  }
  &__discount {
    display: flex;
    color: var(--zoom-color-text-success, #247F40);
    .opc-cart-item__value{
      color: var(--zoom-color-text-success, #247F40);
    }
    font-weight: 450;
    font-size: 14px;
    line-height: 18px;
    justify-content: space-between;
    align-items: center;
    @include media(mobile, tablet) {
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }

    &-tooltip-btn {
      &.zbo-button {
        transform: translateY(1px);
        color: var(--zoom-color-text-neutral, #686F79) !important;
      }
    }
  }
  &__discount &__value::before {
    content: '-';
  }
  &--inline &__text {
    display: inline-block;
  }
}
</style>
